import React, { useState } from 'react';
import { Plus, Users, Loader2, AlertTriangle } from 'lucide-react';
import { Task, TaskStatus, TaskTeam, ACTIVE_COLUMNS, BACKLOG_COLUMN, TaskUser, TEAMS } from '../types';
import { useTaskBoard } from '../hooks/useTaskBoard';
import { useTaskSocket } from '../hooks/useTaskSocket';
import { TaskColumn } from './TaskColumn';
import { TaskDetailModal } from './TaskDetailModal';
import { DeprioritizeModal } from './DeprioritizeModal';
import { CreateTaskModal } from './CreateTaskModal';
import { ChatBox } from './ChatBox';

interface TaskBoardProps {
  currentUser: TaskUser;
  isAdminOrCeo: boolean;
  canUseAIChat?: boolean;
  allUsers: TaskUser[];
}

type TeamFilter = TaskTeam | 'All';

const TEAM_FILTER_COLORS: Record<TaskTeam, string> = {
  Engineering: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
  Finance:     'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  Operations:  'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
  HR:          'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
  Sales:       'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
  KAM:         'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
  Management:  'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
};

const TEAM_FILTER_ACTIVE: Record<TaskTeam, string> = {
  Engineering: 'bg-violet-600 text-white border-violet-600',
  Finance:     'bg-emerald-600 text-white border-emerald-600',
  Operations:  'bg-blue-600 text-white border-blue-600',
  HR:          'bg-pink-600 text-white border-pink-600',
  Sales:       'bg-amber-500 text-white border-amber-500',
  KAM:         'bg-cyan-600 text-white border-cyan-600',
  Management:  'bg-slate-600 text-white border-slate-600',
};

const MOBILE_TABS: { id: TaskStatus; label: string; dot: string; activeStyle: string }[] = [
  { id: 'todo',        label: 'To Do',       dot: 'bg-blue-500',    activeStyle: 'border-blue-600 text-blue-600'       },
  { id: 'in_progress', label: 'In Progress', dot: 'bg-amber-500',   activeStyle: 'border-amber-600 text-amber-600'    },
  { id: 'done',        label: 'Done',        dot: 'bg-emerald-500', activeStyle: 'border-emerald-600 text-emerald-600' },
  { id: 'backlog',     label: 'Backlog',     dot: 'bg-gray-400',    activeStyle: 'border-gray-600 text-gray-700'       },
];

export const TaskBoard: React.FC<TaskBoardProps> = ({ currentUser, isAdminOrCeo, canUseAIChat = false, allUsers }) => {
  const {
    isLoading,
    apiError,
    teams,
    draggedTaskId,
    setDraggedTaskId,
    sortConfig,
    setSortForColumn,
    getTasksByStatus,
    moveTask,
    addTask,
    updateTask,
    addComment,
    totalCounts,
  } = useTaskBoard(currentUser);

  useTaskSocket(currentUser.id);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingDrop, setPendingDrop] = useState<{ taskId: string; targetStatus: TaskStatus } | null>(null);
  const [viewingUserId, setViewingUserId] = useState<number>(currentUser.id);
  const [teamFilter, setTeamFilter] = useState<TeamFilter>('All');
  const [mobileCol, setMobileCol] = useState<TaskStatus>('todo');

  const viewingUser = allUsers.find(u => u.id === viewingUserId) ?? currentUser;

  const filterTasks = (tasks: Task[]) =>
    teamFilter === 'All' ? tasks : tasks.filter(t => t.team === teamFilter);

  const handleDragStart = (taskId: string) => setDraggedTaskId(taskId);
  const handleDragEnd = () => setDraggedTaskId(null);

  const handleDrop = (targetStatus: TaskStatus) => {
    if (!draggedTaskId) return;
    setDraggedTaskId(null);
    if (targetStatus === 'backlog') {
      setPendingDrop({ taskId: draggedTaskId, targetStatus });
    } else {
      moveTask(draggedTaskId, targetStatus);
    }
  };

  const handleDeprioritizeConfirm = (reason: string) => {
    if (!pendingDrop) return;
    moveTask(pendingDrop.taskId, pendingDrop.targetStatus, reason);
    setSelectedTask(prev =>
      prev && prev.id === pendingDrop.taskId ? { ...prev, status: pendingDrop.targetStatus } : prev
    );
    setPendingDrop(null);
  };

  const allActiveTasks = [
    ...getTasksByStatus('todo'),
    ...getTasksByStatus('in_progress'),
    ...getTasksByStatus('done'),
  ];
  const pendingDropTask = pendingDrop
    ? allActiveTasks.find(t => t.id === pendingDrop.taskId)
      ?? getTasksByStatus('backlog').find(t => t.id === pendingDrop.taskId)
      ?? null
    : null;

  const totalTasks = Object.values(totalCounts).reduce((a, b) => a + b, 0);
  const mobileColumn = mobileCol === 'backlog'
    ? BACKLOG_COLUMN
    : (ACTIVE_COLUMNS.find(c => c.id === mobileCol) ?? ACTIVE_COLUMNS[0]);

  if (isLoading) {
    return (
      <div className='flex flex-col items-center justify-center h-64 gap-3'>
        <Loader2 size={28} className='animate-spin text-blue-500' />
        <p className='text-sm text-gray-500'>Loading tasks…</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* API offline banner */}
      {apiError && (
        <div className='flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700'>
          <AlertTriangle size={13} className='flex-shrink-0' />
          Backend unreachable — showing cached data. Changes will sync when the API is back.
        </div>
      )}

      {/* Board header */}
      <div className='flex items-center justify-between mb-4 gap-3'>
        <div className='flex items-center gap-2 sm:gap-4 min-w-0'>
          <div className='min-w-0'>
            <h1 className='text-base sm:text-lg font-bold text-gray-900 truncate'>Task Board</h1>
            <p className='text-xs text-gray-500 mt-0.5 hidden sm:block'>{totalTasks} tasks across all columns</p>
          </div>

          {isAdminOrCeo && (
            <div className='flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 min-w-0'>
              <Users size={13} className='text-gray-400 flex-shrink-0' />
              <span className='text-xs text-gray-500 hidden sm:inline'>Viewing:</span>
              <select
                value={viewingUserId}
                onChange={e => setViewingUserId(Number(e.target.value))}
                className='text-xs font-medium text-gray-800 bg-transparent outline-none cursor-pointer max-w-[90px] sm:max-w-none'
              >
                {allUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.id === currentUser.id ? ' (You)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className='flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm flex-shrink-0'
        >
          <Plus size={15} />
          <span className='hidden sm:inline'>Create Task</span>
          <span className='sm:hidden'>New</span>
        </button>
      </div>

      {/* Department filter bar — horizontal scroll, no wrapping */}
      <div className='flex items-center gap-2 mb-4 overflow-x-auto pb-1'>
        <span className='text-xs text-gray-400 font-medium flex-shrink-0'>Filter:</span>
        <button
          onClick={() => setTeamFilter('All')}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex-shrink-0 ${
            teamFilter === 'All'
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          All
        </button>
        {TEAMS.map(team => (
          <button
            key={team}
            onClick={() => setTeamFilter(teamFilter === team ? 'All' : team)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex-shrink-0 ${
              teamFilter === team ? TEAM_FILTER_ACTIVE[team] : TEAM_FILTER_COLORS[team]
            }`}
          >
            {team}
          </button>
        ))}
      </div>

      {/* Viewing banner */}
      {isAdminOrCeo && viewingUserId !== currentUser.id && (
        <div className='mb-4 px-3 sm:px-4 py-2.5 bg-purple-50 border border-purple-100 rounded-lg flex items-center gap-2'>
          <div className={`w-6 h-6 rounded-full ${viewingUser.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
            {viewingUser.initials}
          </div>
          <p className='text-xs text-purple-700 font-medium truncate'>
            Viewing <span className='font-bold'>{viewingUser.name}&apos;s</span> board
          </p>
          <button
            onClick={() => setViewingUserId(currentUser.id)}
            className='ml-auto text-xs text-purple-600 hover:text-purple-800 font-semibold transition-colors flex-shrink-0'
          >
            Back
          </button>
        </div>
      )}

      {/* ── MOBILE: column tabs + single active column ──────────────────────── */}
      <div className='flex md:hidden flex-col flex-1 min-h-0'>
        {/* Tab bar */}
        <div className='flex border-b border-gray-200 overflow-x-auto mb-3 flex-shrink-0'>
          {MOBILE_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setMobileCol(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                mobileCol === tab.id
                  ? tab.activeStyle
                  : 'border-transparent text-gray-400'
              }`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tab.dot}`} />
              {tab.label}
              <span className='ml-0.5 bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0.5 rounded-full font-bold'>
                {filterTasks(getTasksByStatus(tab.id)).length}
              </span>
            </button>
          ))}
        </div>

        {/* Single active column */}
        <div className='flex-1 overflow-y-auto'>
          <TaskColumn
            column={mobileColumn}
            tasks={filterTasks(getTasksByStatus(mobileCol))}
            count={filterTasks(getTasksByStatus(mobileCol)).length}
            draggedTaskId={draggedTaskId}
            sortConfig={sortConfig[mobileCol]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop}
            onCardClick={task => setSelectedTask(task)}
            onAddTask={() => setShowCreateModal(true)}
            onSortChange={config => setSortForColumn(mobileCol, config)}
            wide={mobileCol === 'backlog'}
          />
        </div>
      </div>

      {/* ── DESKTOP: 3-column grid + backlog ────────────────────────────────── */}
      <div className='hidden md:flex gap-4 overflow-x-auto pb-4'>
        {ACTIVE_COLUMNS.map(column => (
          <TaskColumn
            key={column.id}
            column={column}
            tasks={filterTasks(getTasksByStatus(column.id))}
            count={filterTasks(getTasksByStatus(column.id)).length}
            draggedTaskId={draggedTaskId}
            sortConfig={sortConfig[column.id]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop}
            onCardClick={task => setSelectedTask(task)}
            onAddTask={() => setShowCreateModal(true)}
            onSortChange={config => setSortForColumn(column.id, config)}
          />
        ))}
      </div>

      <div className='hidden md:block mt-2'>
        <TaskColumn
          column={BACKLOG_COLUMN}
          tasks={filterTasks(getTasksByStatus('backlog'))}
          count={filterTasks(getTasksByStatus('backlog')).length}
          draggedTaskId={draggedTaskId}
          sortConfig={sortConfig.backlog}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDrop={handleDrop}
          onCardClick={task => setSelectedTask(task)}
          onAddTask={() => setShowCreateModal(true)}
          onSortChange={config => setSortForColumn('backlog', config)}
          wide
        />
      </div>

      {/* Modals */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          users={allUsers}
          onClose={() => setSelectedTask(null)}
          onUpdate={(taskId, updates) => {
            if (updates.status !== undefined) {
              if (updates.status === 'backlog') {
                setPendingDrop({ taskId, targetStatus: 'backlog' });
              } else {
                moveTask(taskId, updates.status);
                setSelectedTask(prev => (prev ? { ...prev, status: updates.status! } : prev));
              }
            } else {
              updateTask(taskId, updates);
              setSelectedTask(prev => (prev ? { ...prev, ...updates } : prev));
            }
          }}
          onAddComment={addComment}
        />
      )}

      {pendingDrop && pendingDropTask && (
        <DeprioritizeModal
          task={pendingDropTask}
          onConfirm={handleDeprioritizeConfirm}
          onCancel={() => setPendingDrop(null)}
        />
      )}

      {showCreateModal && (
        <CreateTaskModal
          users={allUsers}
          currentUser={currentUser}
          teams={teams}
          onClose={() => setShowCreateModal(false)}
          onSubmit={addTask}
        />
      )}

      {canUseAIChat && (
        <ChatBox
          users={allUsers}
          currentUser={currentUser}
          onCreateTask={data =>
            addTask({
              title: data.title,
              description: data.description,
              assigneeId: data.assigneeId,
              priority: data.priority,
              status: data.status,
              team: data.team,
              dueDate: data.dueDate,
              labels: data.labels,
            })
          }
        />
      )}
    </div>
  );
};
